import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Button from '../../shared/TailwindComponents/Button';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../../shared/TailwindComponents/Table';
import Badge from '../../shared/TailwindComponents/Badge';
import FileUploader from '../../shared/TailwindComponents/FileUploader';
import {
  PiFileTextLight,
  PiImageLight,
  PiFile,
  PiTrashLight,
  PiCloudArrowDownLight,
  PiPlus,
} from 'react-icons/pi';
import { formatDocumentName } from '../../../utils/functions';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, taskAttachmentsTabTourSteps } from '../../shared/FeatureTour';

interface TaskAttachmentsTabContentProps {
  taskDetails: any;
  taskUuid: string;
  userAccessLevel: 'edit' | 'view' | 'owner' | 'admin';
  taskStatus: string;
  attachments: any[];
  setAttachments: React.Dispatch<React.SetStateAction<any[]>>;
  attachmentsLoading: boolean;
  handleOpenUploadModal: () => void;
  handleOpenAttachment: (attachment: any) => Promise<void>;
  handleDeleteAttachment: (attachmentId: string) => void;
  handleFileUpload?: (files: File[]) => Promise<void> | void;
}

const InlineAttachmentUploader: React.FC<{ handleFileUpload: (files: File[]) => Promise<void> | void }> = ({ handleFileUpload }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async () => {
    if (files.length === 0) return;
    setSubmitting(true);
    try {
      await handleFileUpload(files);
      setFiles([]);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="space-y-3">
      <FileUploader files={files} onFilesChange={setFiles} />
      {files.length > 0 && (
        <div className="flex justify-end">
          <Button variant="primary" size="small" onClick={onSubmit} disabled={submitting}>
            {submitting ? t('Uploading...') : t('Upload')}
          </Button>
        </div>
      )}
    </div>
  );
};

const TaskAttachmentsTabContent: React.FC<TaskAttachmentsTabContentProps> = ({
  taskDetails,
  taskUuid,
  userAccessLevel,
  taskStatus,
  attachments,
  setAttachments,
  attachmentsLoading,
  handleOpenUploadModal,
  handleFileUpload,
  handleOpenAttachment,
  handleDeleteAttachment,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'task-attachments-tab',
    steps: taskAttachmentsTabTourSteps,
    readyToShow: !attachmentsLoading,
    autoStartDelay: 600,
  });

  const getFileIcon = (fileType: string) => {
    if (!fileType) return <PiFile className="h-5 w-5 text-gray-500" />;
    const lowerType = fileType.toLowerCase();
    if (lowerType.includes('pdf')) return <PiFileTextLight className="h-5 w-5 text-red-500" />;
    if (lowerType.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(lowerType)) return <PiImageLight className="h-5 w-5 text-green-500" />;
    if (lowerType.includes('word') || ['doc', 'docx', 'dot', 'dotx'].includes(lowerType)) return <PiFileTextLight className="h-5 w-5 text-blue-500" />;
    return <PiFile className="h-5 w-5 text-gray-500" />;
  };

  const filteredAttachments = useMemo(() => {
    return attachments.filter((attachment: any) =>
      (attachment.FileName || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [attachments, searchTerm]);

  const tableData = useMemo(() => {
    return filteredAttachments.map((attachment: any) => {
      const { name: formattedName, extension } = formatDocumentName(attachment.FileName || t('Untitled'));
      const uploadedBy = attachment.uploadedBy;
      const uploaderName = uploadedBy
        ? `${uploadedBy.first_name || ''} ${uploadedBy.last_name || ''}`.trim()
        : null;

      return {
        id: attachment.AttachmentID || attachment.id,
        'Name': formattedName,
        'File Type': extension?.toUpperCase() || 'FILE',
        'Uploaded At': attachment.uploadedAt
          ? dayjs(attachment.uploadedAt).format('MMM DD, YYYY HH:mm')
          : '—',
        'Uploaded By': uploaderName || '—',
        _originalAttachment: attachment,
      };
    });
  }, [filteredAttachments, t]);

  if (attachmentsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-6 w-32 rounded"></div>
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-8 w-24 rounded"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-16 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div data-tour-id="task-attachments-actions">
      <FilterBarWithTabs
        onSearchChange={setSearchTerm}
        noParentPadding={true}
        noParentHeight={true}
        actions={
          userAccessLevel !== 'view' && (taskStatus !== 'completed' && taskStatus !== 'deleted')
            ? [
                {
                  label: t('Upload File'),
                  icon: <PiPlus className="h-4 w-4" />,
                  onClick: handleOpenUploadModal,
                },
              ]
            : []
        }
      />
      </div>
      <div data-tour-id="task-attachments-table">
      {attachments.length === 0 ? (
        userAccessLevel !== 'view' && taskStatus !== 'completed' && taskStatus !== 'deleted' && handleFileUpload ? (
          <div className="flex justify-center py-8 px-4">
            <div className="w-full max-w-xl">
              <InlineAttachmentUploader handleFileUpload={handleFileUpload} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-300 dark:border-gray-700 rounded p-12">
            <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
              <PiCloudArrowDownLight className="h-6 w-6 text-gray-500" />
            </div>
            <h3 className="text-lg text-gray-900 dark:text-gray-100 mb-2">
              {t('No attachments')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('Upload files to attach them to this task.')}
            </p>
          </div>
        )
      ) : (
        <Table
          columns={['Name', 'File Type', 'Uploaded At', 'Uploaded By', 'Actions']}
          data={tableData}
          total_items={filteredAttachments.length}
          setIsShowing={true}
          total_showing_items={attachments.length}
          showingLabel={attachments.length === 1 ? 'file' : 'files'}
          renderCell={(column: string, value: any, row: any) => {
            if (column === 'Name') {
              const attachment = row._originalAttachment;
              return (
                <div className="flex items-center gap-2 min-w-0">
                  {getFileIcon(attachment.FileType)}
                  <span className="text-sm text-gray-900 dark:text-gray-200 truncate">
                    {value}
                  </span>
                </div>
              );
            }
            if (column === 'File Type') {
              return (
                <Badge color="gray" className="text-xs">
                  {value}
                </Badge>
              );
            }
            return undefined;
          }}
          onRowClick={(row: any) => {
            if (row._originalAttachment) handleOpenAttachment(row._originalAttachment);
          }}
          actions={[
            {
              label: t('Open'),
              icon: <PiFileTextLight className="h-4 w-4" />,
              onClick: (row: any) => {
                if (row._originalAttachment) handleOpenAttachment(row._originalAttachment);
              }
            },
            {
              label: t('Download'),
              icon: <PiCloudArrowDownLight className="h-4 w-4" />,
              onClick: (row: any) => {
                if (row._originalAttachment) {
                  const link = document.createElement('a');
                  link.href = row._originalAttachment.FileURL;
                  link.download = row._originalAttachment.FileName;
                  link.click();
                }
              }
            },
            ...(taskStatus === 'todo' && userAccessLevel !== 'view' ? [{
              label: t('Remove'),
              icon: <PiTrashLight className="h-4 w-4" />,
              onClick: (row: any) => {
                if (row._originalAttachment) {
                  handleDeleteAttachment(row._originalAttachment.AttachmentID || row._originalAttachment.id);
                }
              },
              isDestructive: true
            }] : [])
          ]}
          resizableColumns={true}
          defaultColumnWidths={{
            'Name': 250,
            'File Type': 100,
            'Uploaded At': 180,
            'Uploaded By': 180,
            'Actions': 48,
          }}
          max="max-h-[calc(100vh-240px)]"
          min="min-h-[calc(100vh-240px)]"
          emptyState={{
            icon: <PiCloudArrowDownLight />,
            headline: t('No matching files'),
            subtext: t('Try a different search term.'),
          }}
        />
      )}
      </div>
      <FeatureTour
        steps={taskAttachmentsTabTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default TaskAttachmentsTabContent;
